import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  alpha
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { AttendanceStatus, InformationType } from '../../types';

// ============================================================================
// Edit one cell. Used from the monthly grid when an admin needs to correct a
// single student/day. Backed by PUT /api/student-attendance/:id/override on
// the server side, which appends to the audit trail.
// ============================================================================

const STATUS_OPTIONS: { value: AttendanceStatus; label: string; color: string }[] = [
  { value: AttendanceStatus.PRESENT, label: 'Present', color: '#22c55e' },
  { value: AttendanceStatus.LATE, label: 'Late', color: '#f59e0b' },
  { value: AttendanceStatus.HALF_DAY, label: 'Half Day', color: '#3b82f6' },
  { value: AttendanceStatus.ABSENT, label: 'Absent', color: '#ef4444' }
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (payload: { status: AttendanceStatus; informationType?: InformationType; remarks?: string }) => Promise<void>;
  initial: {
    studentName?: string;
    rollNumber?: string;
    date?: string;
    status?: AttendanceStatus;
    informationType?: InformationType;
    remarks?: string;
  };
}

const StudentOverrideAttendanceModal: React.FC<Props> = ({ open, onClose, onSave, initial }) => {
  const [status, setStatus] = useState<AttendanceStatus>(initial.status ?? AttendanceStatus.PRESENT);
  const [informationType, setInformationType] = useState<InformationType | undefined>(initial.informationType);
  const [remarks, setRemarks] = useState<string>(initial.remarks ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(initial.status ?? AttendanceStatus.PRESENT);
      setInformationType(initial.informationType);
      setRemarks(initial.remarks ?? '');
    }
  }, [open, initial.status, initial.informationType, initial.remarks]);

  const needsInfoType = [AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(status);
  const disabled = saving || (needsInfoType && !informationType);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave({ status, informationType: needsInfoType ? informationType : undefined, remarks: remarks || undefined });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} fontSize="1.05rem">Edit Attendance</Typography>
          <Typography variant="caption" color="text.secondary">
            {initial.studentName ? `${initial.studentName}${initial.rollNumber ? ` · #${initial.rollNumber}` : ''}` : 'Update record'}
            {initial.date ? ` · ${initial.date}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" edge="end"><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Status</Typography>
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {STATUS_OPTIONS.map(opt => (
            <Chip
              key={opt.value}
              label={opt.label}
              onClick={() => setStatus(opt.value)}
              variant={status === opt.value ? 'filled' : 'outlined'}
              sx={{
                bgcolor: status === opt.value ? opt.color : 'transparent',
                color: status === opt.value ? '#fff' : opt.color,
                borderColor: opt.color,
                '&:hover': { bgcolor: status === opt.value ? opt.color : alpha(opt.color, 0.1) }
              }}
            />
          ))}
        </Box>

        {needsInfoType && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={500} gutterBottom>Type</Typography>
            <RadioGroup
              row
              value={informationType || ''}
              onChange={(e) => setInformationType(e.target.value as InformationType)}
            >
              <FormControlLabel value={InformationType.INFORMED} control={<Radio size="small" />} label="Informed" />
              <FormControlLabel value={InformationType.UNINFORMED} control={<Radio size="small" />} label="Uninformed" />
            </RadioGroup>
          </Box>
        )}

        <TextField
          fullWidth
          placeholder="Optional remarks"
          multiline
          rows={2}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={disabled} sx={{ borderRadius: 2, px: 4 }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentOverrideAttendanceModal;
